import { storeToRefs } from 'pinia'
import type { RouterName } from '@/enums/router-name'
import {
  routerSettingSchema,
  type RouterSetting
} from '@/models/router-setting'
import { useRouterStorageStore } from '@/stores/use-router-storage-store'
import { isType } from '~library/utils/functions'

export type RouterTo<T extends RouterName> = {
  name: T
} & (T extends keyof RouterSetting ? RouterSetting[T] : object)

type Params<T extends RouterName> = T extends keyof RouterSetting
  ? RouterSetting[T] extends { params: unknown }
    ? RouterSetting[T]['params']
    : unknown
  : unknown

type Query<T extends RouterName> = T extends keyof RouterSetting
  ? RouterSetting[T] extends { query: unknown }
    ? RouterSetting[T]['query']
    : unknown
  : unknown

/** 提供型別安全的路由操作，整合 cacheRouter 機制 */
export function useRouterUtils<T extends RouterName>() {
  const router = useRouter()
  const route = useRoute()

  const { cacheRouterData } = useRouterStorageStore()
  const { cacheRouter, storageRouterData } = storeToRefs(
    useRouterStorageStore()
  )

  /** 當前路由的 meta 資料 */
  const routerMeta = computed(() => route.meta)

  /** 當前路由名稱 */
  const routerName = computed((): RouterName => route.name as RouterName)

  /** 當前路由參數，優先從 cacheRouter 取得 */
  const params = computed(() => {
    if (
      isType(routerName.value, routerSettingSchema.keyof()) &&
      cacheRouter.value.includes(routerName.value)
    ) {
      return (
        storageRouterData.value?.[routerName.value] as { params?: unknown }
      )?.params as Params<T>
    }

    return route.params as Params<T>
  })

  /** 當前路由 query 參數，優先從 cacheRouter 取得 */
  const query = computed(() => {
    if (
      isType(routerName.value, routerSettingSchema.keyof()) &&
      cacheRouter.value.includes(routerName.value)
    ) {
      return (
        storageRouterData.value?.[routerName.value] as { query?: unknown }
      )?.query as Query<T>
    }

    return route.query as Query<T>
  })

  /** 回到上一頁 */
  const back = () => router.back()

  /** 導航至指定路由 */
  const push = <T extends RouterName>(to: RouterTo<T>) => {
    return router.push(cacheRouterData(to))
  }

  /** 在新分頁開啟指定路由 */
  const newTab = <T extends RouterName>(to: RouterTo<T>) => {
    return window.open(router.resolve(cacheRouterData(to)).href, '_blank')
  }

  /** 取代當前路由 */
  const replace = <T extends RouterName>(to: RouterTo<T>) => {
    return router.replace(cacheRouterData(to))
  }

  return {
    /** 回到上一頁 */
    back,
    /** 導航至指定路由 */
    push,
    /** 在新分頁開啟指定路由 */
    newTab,
    /** 取代當前路由 */
    replace,
    /** 當前路由的 meta 資料 */
    routerMeta,
    /** 當前路由名稱 */
    routerName,
    /** 當前路由 query 參數，優先從 cacheRouter 取得 */
    query,
    /** 當前路由參數，優先從 cacheRouter 取得 */
    params
  }
}
